import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiRequest } from '@/utils/api-request';
import { Briefcase, X, ArrowLeft, Search, ChevronDown, Check } from 'lucide-react';
import { showToast } from '@/components/ui/Toast';
import Button from '@/components/ui/Button';

interface Category {
    id: number;
    name: string;
}

interface Board {
    board_id: number;
    board_name: string;
}

interface PostJobFormData {
    title: string;
    jobCategoryId: number | '';
    teachingCategoryIds: number[];
    teachinglevel: number | '';
    board: number | '';
    vacancies: number | '';
    salary_type: number;
    min_salary: number | '';
    max_salary: number | '';
    jobType: number | '';
    description: string;
    exp_level: number;
    min_exp: number | '';
    max_exp: number | '';
    deadline: string;
    is_verified: number;
    urgentHiring: number;
    status: number;
}

// ==================== MULTI-SELECT DROPDOWN COMPONENT ====================
interface MultiSelectDropdownProps {
    options: Category[];
    selectedIds: number[];
    onChange: (ids: number[]) => void;
    placeholder?: string;
    label: string;
    required?: boolean;
    error?: string;
    loading?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    options,
    selectedIds,
    onChange,
    placeholder = 'Select options...',
    label,
    required = false,
    error,
    loading = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Filter options based on search
    const filteredOptions = options.filter(option =>
        option.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Toggle selection
    const toggleOption = (id: number) => {
        if (selectedIds.includes(id)) {
            onChange(selectedIds.filter(selectedId => selectedId !== id));
        } else {
            onChange([...selectedIds, id]);
        }
    };

    // Select all filtered options
    const selectAll = () => {
        const filteredIds = filteredOptions.map(o => o.id);
        const newIds = [...new Set([...selectedIds, ...filteredIds])];
        onChange(newIds);
    };

    // Clear all selections
    const clearAll = () => {
        onChange([]);
    };

    // Get selected option names
    const getSelectedNames = () => {
        return selectedIds
            .map(id => options.find(o => o.id === id)?.name)
            .filter(Boolean);
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {label} {required && <span className="text-red-500">*</span>}
            </label>

            {/* Trigger Button */}
            <div
                onClick={() => {
                    setIsOpen(!isOpen);
                    if (!isOpen) setTimeout(() => inputRef.current?.focus(), 100);
                }}
                className={`w-full min-h-[42px] px-3 py-2 border rounded-lg cursor-pointer flex items-center justify-between gap-2 transition-all ${
                    error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 hover:border-gray-400'
                } ${isOpen ? 'ring-2 ring-brandColor border-brandColor' : ''}`}
            >
                <div className="flex-1 flex flex-wrap gap-1">
                    {selectedIds.length === 0 ? (
                        <span className="text-gray-400 text-sm">{placeholder}</span>
                    ) : selectedIds.length <= 3 ? (
                        getSelectedNames().map((name, idx) => (
                            <span
                                key={idx}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-brandColor/10 text-brandColor text-xs rounded-full"
                            >
                                {name}
                                <X
                                    size={12}
                                    className="cursor-pointer hover:text-red-500"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const id = options.find(o => o.name === name)?.id;
                                        if (id) toggleOption(id);
                                    }}
                                />
                            </span>
                        ))
                    ) : (
                        <span className="text-sm text-gray-700">
                            {selectedIds.length} items selected
                        </span>
                    )}
                </div>
                <ChevronDown
                    size={18}
                    className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                />
            </div>

            {/* Dropdown Panel */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-100">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                ref={inputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search..."
                                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                            />
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="px-2 py-1.5 border-b border-gray-100 flex gap-2">
                        <button
                            type="button"
                            onClick={selectAll}
                            className="text-xs text-brandColor hover:underline"
                        >
                            Select All
                        </button>
                        <span className="text-gray-300">|</span>
                        <button
                            type="button"
                            onClick={clearAll}
                            className="text-xs text-red-500 hover:underline"
                        >
                            Clear All
                        </button>
                    </div>

                    {/* Options List */}
                    <div className="max-h-48 overflow-y-auto">
                        {loading ? (
                            <div className="px-3 py-4 text-center text-sm text-gray-500">
                                Loading options...
                            </div>
                        ) : filteredOptions.length === 0 ? (
                            <div className="px-3 py-4 text-center text-sm text-gray-500">
                                {searchTerm ? 'No options found' : 'No options available'}
                            </div>
                        ) : (
                            filteredOptions.map((option) => {
                                const isSelected = selectedIds.includes(option.id);
                                return (
                                    <div
                                        key={option.id}
                                        onClick={() => toggleOption(option.id)}
                                        className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                                            isSelected ? 'bg-brandColor/5' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <div
                                            className={`w-4 h-4 rounded border flex items-center justify-center transition-colors ${
                                                isSelected
                                                    ? 'bg-brandColor border-brandColor'
                                                    : 'border-gray-300'
                                            }`}
                                        >
                                            {isSelected && <Check size={12} className="text-white" />}
                                        </div>
                                        <span className={`text-sm ${isSelected ? 'text-brandColor font-medium' : 'text-gray-700'}`}>
                                            {option.name}
                                        </span>
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Footer */}
                    {selectedIds.length > 0 && (
                        <div className="px-3 py-2 border-t border-gray-100 bg-gray-50 text-xs text-gray-500">
                            {selectedIds.length} selected
                        </div>
                    )}
                </div>
            )}

            {/* Error Message */}
            {error && <p className="text-red-500 text-xs mt-1">{error}</p>}

            {/* Selected Tags (shown below when more than 3) */}
            {selectedIds.length > 3 && (
                <div className="flex flex-wrap gap-1 mt-2">
                    {getSelectedNames().map((name, idx) => (
                        <span
                            key={idx}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-brandColor/10 text-brandColor text-xs rounded-full"
                        >
                            {name}
                            <X
                                size={12}
                                className="cursor-pointer hover:text-red-500"
                                onClick={() => {
                                    const id = options.find(o => o.name === name)?.id;
                                    if (id) toggleOption(id);
                                }}
                            />
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
};

// ==================== MAIN COMPONENT ====================
const PostJob: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const schoolId = id ? parseInt(id) : null;

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Dropdown data
    const [categories, setCategories] = useState<Category[]>([]);
    const [teachingCategories, setTeachingCategories] = useState<Category[]>([]);
    const [teachingLevels, setTeachingLevels] = useState<Category[]>([]);
    const [boards, setBoards] = useState<Board[]>([]);
    const [loadingTeachingCategories, setLoadingTeachingCategories] = useState(false);

    // Form Data
    const initialFormData: PostJobFormData = {
        title: '',
        jobCategoryId: '',
        teachingCategoryIds: [],
        teachinglevel: '',
        board: '',
        vacancies: '',
        salary_type: 3,
        min_salary: '',
        max_salary: '',
        jobType: '',
        description: '',
        exp_level: 1,
        min_exp: '',
        max_exp: '',
        deadline: '',
        is_verified: 0,
        urgentHiring: 0,
        status: 1,
    };

    const [formData, setFormData] = useState<PostJobFormData>(initialFormData);
    const [formErrors, setFormErrors] = useState<Record<string, string>>({});

    // Fetch dropdown data on mount
    useEffect(() => {
        fetchCategories();
        fetchTeachingLevels();
        fetchBoards();
    }, []);

    // Fetch teaching categories when job category changes
    useEffect(() => {
        if (formData.jobCategoryId && (formData.jobCategoryId === 22 || formData.jobCategoryId === 23)) {
            fetchTeachingCategories(formData.jobCategoryId);
        } else {
            setTeachingCategories([]);
        }
    }, [formData.jobCategoryId]);

    // Fetch dropdown data
    const fetchCategories = async () => {
        try {
            const response = await apiRequest('/api/jobs/categories', { method: 'GET' });
            if (response.success) {
                setCategories(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    const fetchTeachingCategories = async (categoryId: number) => {
        try {
            setLoadingTeachingCategories(true);
            const response = await apiRequest(`/api/jobs/teaching_categories/${categoryId}`, { method: 'GET' });
            if (response.success) {
                setTeachingCategories(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching teaching categories:', error);
        } finally {
            setLoadingTeachingCategories(false);
        }
    };

    const fetchTeachingLevels = async () => {
        try {
            const response = await apiRequest('/api/jobs/levels', { method: 'GET' });
            if (response.success) {
                setTeachingLevels(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching teaching levels:', error);
        }
    };

    const fetchBoards = async () => {
        try {
            const response = await apiRequest('/api/jobs/boards', { method: 'GET' });
            if (response.success) {
                setBoards(response.data || []);
            }
        } catch (error) {
            console.error('Error fetching boards:', error);
        }
    };

    // Handle form input changes
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;

        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({ ...prev, [name]: checked ? 1 : 0 }));
        } else if (type === 'number') {
            setFormData(prev => ({ ...prev, [name]: value === '' ? '' : parseInt(value) }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Handle teaching category change (for multi-select)
    const handleTeachingCategoryChange = (ids: number[]) => {
        setFormData(prev => ({ ...prev, teachingCategoryIds: ids }));
        if (formErrors.teachingCategoryIds) {
            setFormErrors(prev => ({ ...prev, teachingCategoryIds: '' }));
        }
    };

    // Validate form
    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};

        // Basic Info
        if (!formData.title.trim()) {
            errors.title = 'Job title is required';
        } else if (formData.title.length < 5) {
            errors.title = 'Title must be at least 5 characters';
        }

        if (!formData.jobCategoryId) {
            errors.jobCategoryId = 'Category is required';
        }

        if (formData.jobCategoryId === 22 || formData.jobCategoryId === 23) {
            if (formData.teachingCategoryIds.length === 0) {
                errors.teachingCategoryIds = 'Please select at least one teaching category';
            }
        }

        if (formData.jobCategoryId === 22) {
            if (!formData.teachinglevel) errors.teachinglevel = 'Teaching level is required';
            if (!formData.board) errors.board = 'Board is required';
        }

        // Salary & Type
        if (!formData.jobType) {
            errors.jobType = 'Job type is required';
        }

        if (formData.salary_type === 1 && !formData.min_salary) {
            errors.min_salary = 'Salary is required';
        }

        if (formData.salary_type === 2) {
            if (!formData.min_salary) errors.min_salary = 'Min salary is required';
            if (!formData.max_salary) errors.max_salary = 'Max salary is required';
            if (formData.min_salary && formData.max_salary && Number(formData.max_salary) <= Number(formData.min_salary)) {
                errors.max_salary = 'Max salary must be greater than min salary';
            }
        }

        // Experience
        if (formData.exp_level === 2 && !formData.min_exp) {
            errors.min_exp = 'Experience is required';
        }

        if (formData.exp_level === 3) {
            if (!formData.min_exp) errors.min_exp = 'Min experience is required';
            if (!formData.max_exp) errors.max_exp = 'Max experience is required';
            if (formData.min_exp && formData.max_exp && Number(formData.max_exp) <= Number(formData.min_exp)) {
                errors.max_exp = 'Max experience must be greater than min experience';
            }
        }

        // Description
        if (!formData.description.trim()) {
            errors.description = 'Job description is required';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Submit form
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            showToast('Please fix the form errors', 'error');
            // Scroll to first error
            const firstError = document.querySelector('.text-red-500');
            firstError?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        if (!schoolId) {
            showToast('School ID is required', 'error');
            return;
        }

        setIsSubmitting(true);

        try {
            const payload = {
                title: formData.title,
                jobCategoryId: formData.jobCategoryId,
                teachingCategoryIds: formData.teachingCategoryIds,
                teachinglevel: formData.teachinglevel || 0,
                board: formData.board || 0,
                vacancies: formData.vacancies || 0,
                salary_type: formData.salary_type,
                min_salary: formData.min_salary || 0,
                max_salary: formData.max_salary || 0,
                jobType: formData.jobType || 0,
                description: formData.description,
                exp_level: formData.exp_level,
                min_exp: formData.min_exp || 0,
                max_exp: formData.max_exp || 0,
                deadline: formData.deadline || null,
                slate_id_fk: schoolId,
                is_verified: formData.is_verified,
                urgentHiring: formData.urgentHiring,
            };

            const response = await apiRequest('/api/jobs/craete_job', {
                method: 'POST',
                body: JSON.stringify(payload),
            });

            if (response.success) {
                showToast('Job posted successfully!', 'success');
                navigate(`/schools/${schoolId}/jobs/list`);
            } else {
                showToast(response.message || 'Failed to post job', 'error');
            }
        } catch (error) {
            console.error('Error posting job:', error);
            showToast('Failed to post job', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        navigate(`/schools/${schoolId}/jobs`);
    };

    // Conditional fields
    const showTeachingFields = formData.jobCategoryId === 22 || formData.jobCategoryId === 23;
    const showTeachingLevelAndBoard = formData.jobCategoryId === 22;

    return (
        <div className="h-full flex flex-col bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 px-8 py-4 flex-shrink-0">
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleCancel}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft size={20} className="text-gray-600" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-brandColor rounded-lg text-white">
                            <Briefcase className="h-5 w-5" />
                        </div>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">Post New Job</h1>
                            <p className="text-xs text-gray-500">Create a new job posting for this school</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-auto">
                <div className="max-w-4xl mx-auto py-6 px-8">
                    <form onSubmit={handleSubmit} className="space-y-6">

                        {/* ==================== BASIC INFO SECTION ==================== */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                Basic Information
                            </h2>
                            <div className="space-y-4">
                                {/* Job Title */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Job Title <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="We are hiring CBSE teachers"
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent ${formErrors.title ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
                                </div>

                                {/* Category */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Category <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="jobCategoryId"
                                        value={formData.jobCategoryId}
                                        onChange={(e) => {
                                            handleInputChange(e);
                                            setFormData(prev => ({ ...prev, jobCategoryId: parseInt(e.target.value) || '', teachingCategoryIds: [] }));
                                        }}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent ${formErrors.jobCategoryId ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        <option value="">Select a Category</option>
                                        {categories.map((cat) => (
                                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                                        ))}
                                    </select>
                                    {formErrors.jobCategoryId && <p className="text-red-500 text-xs mt-1">{formErrors.jobCategoryId}</p>}
                                </div>

                                {/* Teaching Categories - Multi-Select Dropdown */}
                                {showTeachingFields && (
                                    <MultiSelectDropdown
                                        options={teachingCategories}
                                        selectedIds={formData.teachingCategoryIds}
                                        onChange={handleTeachingCategoryChange}
                                        placeholder="Search and select teaching categories..."
                                        label="Teaching Category"
                                        required
                                        error={formErrors.teachingCategoryIds}
                                        loading={loadingTeachingCategories}
                                    />
                                )}

                                {/* Teaching Level and Board */}
                                {showTeachingLevelAndBoard && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Teaching Level <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                name="teachinglevel"
                                                value={formData.teachinglevel}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent ${formErrors.teachinglevel ? 'border-red-500' : 'border-gray-300'}`}
                                            >
                                                <option value="">Select a Level</option>
                                                {teachingLevels.map((level) => (
                                                    <option key={level.id} value={level.id}>{level.name}</option>
                                                ))}
                                            </select>
                                            {formErrors.teachinglevel && <p className="text-red-500 text-xs mt-1">{formErrors.teachinglevel}</p>}
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Board <span className="text-red-500">*</span>
                                            </label>
                                            <select
                                                name="board"
                                                value={formData.board}
                                                onChange={handleInputChange}
                                                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent ${formErrors.board ? 'border-red-500' : 'border-gray-300'}`}
                                            >
                                                <option value="">Select a Board</option>
                                                {boards.map((board) => (
                                                    <option key={board.board_id} value={board.board_id}>{board.board_name}</option>
                                                ))}
                                            </select>
                                            {formErrors.board && <p className="text-red-500 text-xs mt-1">{formErrors.board}</p>}
                                        </div>
                                    </div>
                                )}

                                {/* Vacancies and Deadline */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Vacancies</label>
                                        <input
                                            type="number"
                                            name="vacancies"
                                            value={formData.vacancies}
                                            onChange={handleInputChange}
                                            placeholder="5"
                                            min="0"
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Deadline</label>
                                        <input
                                            type="date"
                                            name="deadline"
                                            value={formData.deadline}
                                            onChange={handleInputChange}
                                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ==================== SALARY & TYPE SECTION ==================== */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                Salary & Job Type
                            </h2>
                            <div className="space-y-4">
                                {/* Job Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Job Type <span className="text-red-500">*</span>
                                    </label>
                                    <select
                                        name="jobType"
                                        value={formData.jobType}
                                        onChange={handleInputChange}
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent ${formErrors.jobType ? 'border-red-500' : 'border-gray-300'}`}
                                    >
                                        <option value="">Select Job Type</option>
                                        <option value={1}>Full Time</option>
                                        <option value={2}>Part Time</option>
                                    </select>
                                    {formErrors.jobType && <p className="text-red-500 text-xs mt-1">{formErrors.jobType}</p>}
                                </div>

                                {/* Salary Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Salary Type</label>
                                    <div className="flex flex-wrap gap-4 mb-3">
                                        {[
                                            { value: 1, label: 'Fixed Salary' },
                                            { value: 2, label: 'Salary Range' },
                                            { value: 3, label: 'Negotiable' },
                                        ].map((option) => (
                                            <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="salary_type"
                                                    value={option.value}
                                                    checked={formData.salary_type === option.value}
                                                    onChange={() => setFormData(prev => ({ ...prev, salary_type: option.value }))}
                                                    className="text-brandColor focus:ring-brandColor"
                                                />
                                                <span className="text-sm">{option.label}</span>
                                            </label>
                                        ))}
                                    </div>

                                    {(formData.salary_type === 1 || formData.salary_type === 2) && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    {formData.salary_type === 1 ? 'Salary (₹)' : 'Min Salary (₹)'} <span className="text-red-500">*</span>
                                                </label>
                                                <input
                                                    type="number"
                                                    name="min_salary"
                                                    value={formData.min_salary}
                                                    onChange={handleInputChange}
                                                    placeholder="25000"
                                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent ${formErrors.min_salary ? 'border-red-500' : 'border-gray-300'}`}
                                                />
                                                {formErrors.min_salary && <p className="text-red-500 text-xs mt-1">{formErrors.min_salary}</p>}
                                            </div>
                                            {formData.salary_type === 2 && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Max Salary (₹) <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        name="max_salary"
                                                        value={formData.max_salary}
                                                        onChange={handleInputChange}
                                                        placeholder="45000"
                                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent ${formErrors.max_salary ? 'border-red-500' : 'border-gray-300'}`}
                                                    />
                                                    {formErrors.max_salary && <p className="text-red-500 text-xs mt-1">{formErrors.max_salary}</p>}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Checkboxes */}
                                <div className="flex flex-wrap gap-6 pt-4 border-t border-gray-100">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="is_verified"
                                            checked={formData.is_verified === 1}
                                            onChange={(e) => setFormData(prev => ({ ...prev, is_verified: e.target.checked ? 1 : 0 }))}
                                            className="rounded border-gray-300 text-brandColor focus:ring-brandColor"
                                        />
                                        <span className="text-sm text-gray-700">Verified Job</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            name="urgentHiring"
                                            checked={formData.urgentHiring === 1}
                                            onChange={(e) => setFormData(prev => ({ ...prev, urgentHiring: e.target.checked ? 1 : 0 }))}
                                            className="rounded border-gray-300 text-brandColor focus:ring-brandColor"
                                        />
                                        <span className="text-sm text-gray-700">Urgent Hiring</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* ==================== EXPERIENCE SECTION ==================== */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                Experience Requirements
                            </h2>
                            <div className="space-y-4">
                                {/* Experience Level */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Experience Level</label>
                                    <div className="flex flex-wrap gap-4 mb-3">
                                        {[
                                            { value: 1, label: 'Fresher' },
                                            { value: 2, label: 'Minimum Experience' },
                                            { value: 3, label: 'Experience Range' },
                                        ].map((option) => (
                                            <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                                                <input
                                                    type="radio"
                                                    name="exp_level"
                                                    value={option.value}
                                                    checked={formData.exp_level === option.value}
                                                    onChange={() => setFormData(prev => ({ ...prev, exp_level: option.value, min_exp: '', max_exp: '' }))}
                                                    className="text-brandColor focus:ring-brandColor"
                                                />
                                                <span className="text-sm">{option.label}</span>
                                            </label>
                                        ))}
                                    </div>

                                    {(formData.exp_level === 2 || formData.exp_level === 3) && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                                    Min Experience (years) <span className="text-red-500">*</span>
                                                </label>
                                                <select
                                                    name="min_exp"
                                                    value={formData.min_exp}
                                                    onChange={handleInputChange}
                                                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent ${formErrors.min_exp ? 'border-red-500' : 'border-gray-300'}`}
                                                >
                                                    <option value="">Select Year</option>
                                                    {Array.from({ length: 20 }, (_, i) => i + 1).map((year) => (
                                                        <option key={year} value={year}>{year}{year === 20 ? '+' : ''} year{year > 1 ? 's' : ''}</option>
                                                    ))}
                                                </select>
                                                {formErrors.min_exp && <p className="text-red-500 text-xs mt-1">{formErrors.min_exp}</p>}
                                            </div>
                                            {formData.exp_level === 3 && (
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Max Experience (years) <span className="text-red-500">*</span>
                                                    </label>
                                                    <select
                                                        name="max_exp"
                                                        value={formData.max_exp}
                                                        onChange={handleInputChange}
                                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent ${formErrors.max_exp ? 'border-red-500' : 'border-gray-300'}`}
                                                    >
                                                        <option value="">Select Year</option>
                                                        {Array.from({ length: 20 }, (_, i) => i + 1).map((year) => (
                                                            <option key={year} value={year}>{year}{year === 20 ? '+' : ''} year{year > 1 ? 's' : ''}</option>
                                                        ))}
                                                    </select>
                                                    {formErrors.max_exp && <p className="text-red-500 text-xs mt-1">{formErrors.max_exp}</p>}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Experience Summary */}
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <h4 className="text-sm font-medium text-blue-800 mb-1">Experience Summary</h4>
                                    <p className="text-sm text-blue-700">
                                        {formData.exp_level === 1 && 'This job is suitable for freshers with no prior experience required.'}
                                        {formData.exp_level === 2 && formData.min_exp && `Candidates need minimum ${formData.min_exp} year${Number(formData.min_exp) > 1 ? 's' : ''} of experience.`}
                                        {formData.exp_level === 2 && !formData.min_exp && 'Please select minimum experience required.'}
                                        {formData.exp_level === 3 && formData.min_exp && formData.max_exp && `Candidates need ${formData.min_exp} to ${formData.max_exp} years of experience.`}
                                        {formData.exp_level === 3 && (!formData.min_exp || !formData.max_exp) && 'Please select experience range.'}
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* ==================== DESCRIPTION SECTION ==================== */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                                Job Description
                            </h2>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Description <span className="text-red-500">*</span>
                                    </label>
                                    <textarea
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        rows={8}
                                        placeholder="Enter detailed job description including responsibilities, requirements, benefits, etc..."
                                        className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brandColor focus:border-transparent ${formErrors.description ? 'border-red-500' : 'border-gray-300'}`}
                                    />
                                    {formErrors.description && <p className="text-red-500 text-xs mt-1">{formErrors.description}</p>}
                                    <p className="text-xs text-gray-500 mt-1">{formData.description.length} characters</p>
                                </div>

                                {/* Tips */}
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <h4 className="text-sm font-medium text-gray-800 mb-2">Tips for a great job description:</h4>
                                    <ul className="text-sm text-gray-600 space-y-1 list-disc list-inside">
                                        <li>Clearly describe the role and responsibilities</li>
                                        <li>List required qualifications and skills</li>
                                        <li>Mention any benefits or perks</li>
                                        <li>Include working hours and location details</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* ==================== SUBMIT BUTTONS ==================== */}
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-4">
                            <div className="flex gap-3 justify-end">
                                <Button type="button" variant="outline" onClick={handleCancel}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="bg-brandColor hover:bg-brandColor/90 text-white"
                                >
                                    {isSubmitting ? 'Posting...' : 'Post Job'}
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PostJob;